import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Users, TrendingUp, DollarSign, ChevronDown, ChevronRight, Search, CheckCircle2, Clock, Lock, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import DateRangePicker from './DateRangePicker';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Downlines({ userInfo }) {
  const [downlines, setDownlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDownline, setSelectedDownline] = useState(null);
  const [expandedPolicies, setExpandedPolicies] = useState({});
  
  // Downlines table state
  const [downlineSearch, setDownlineSearch] = useState('');
  const [downlineSalesFilter, setDownlineSalesFilter] = useState('all'); // all, with-sales, without-sales
  const [downlineStatusFilter, setDownlineStatusFilter] = useState([]);
  const [downlinePage, setDownlinePage] = useState(1);
  const [downlinesPerPage, setDownlinesPerPage] = useState(20);
  
  // Policies table state
  const [policySearch, setPolicySearch] = useState('');
  const [policyStatusFilter, setPolicyStatusFilter] = useState([]);
  const [policyPaidStatusFilter, setPolicyPaidStatusFilter] = useState([]);
  const [policyDateRange, setPolicyDateRange] = useState(null);
  const [policyPage, setPolicyPage] = useState(1);
  const [policiesPerPage, setPoliciesPerPage] = useState(20);

  // Dashboard stats
  const [dashboardStats, setDashboardStats] = useState({
    totalPolicies: 0,
    totalDownlines: 0,
    totalAnnualPremium: 0,
    approved: { count: 0, premium: 0 },
    underwriting: { count: 0, premium: 0 },
    followUp: { count: 0, premium: 0 },
    cancelledBeforeDraft: { count: 0, premium: 0 },
    declined: { count: 0, premium: 0 },
    lapsedPending: { count: 0, premium: 0 },
    lapsed: { count: 0, premium: 0 },
    notTaken: { count: 0, premium: 0 }
  });

  const calculateDashboardStats = (downlinesData) => {
    let totalDownlines = downlinesData.length;
    let totalPolicies = 0;
    let totalAnnualPremium = 0;
    let approved = { count: 0, premium: 0 };
    let underwriting = { count: 0, premium: 0 };
    let followUp = { count: 0, premium: 0 };
    let cancelledBeforeDraft = { count: 0, premium: 0 };
    let declined = { count: 0, premium: 0 };
    let lapsedPending = { count: 0, premium: 0 };
    let lapsed = { count: 0, premium: 0 };
    let notTaken = { count: 0, premium: 0 };

    downlinesData.forEach(downline => {
      const policiesRaw = downline.policies_of_agent || [];
      const policies = Array.isArray(policiesRaw) ? policiesRaw : [];
      
      policies.forEach(policy => {
        totalPolicies++;
        const premium = parseFloat(policy.annual_premium) || 0;
        totalAnnualPremium += premium;
        
        const status = (policy.policyStatus || '').toLowerCase();
        
        if (status.includes('approved') || status.includes('active')) {
          approved.count++;
          approved.premium += premium;
        } else if (status.includes('underwriting')) {
          underwriting.count++;
          underwriting.premium += premium;
        } else if (status.includes('follow')) {
          followUp.count++;
          followUp.premium += premium;
        } else if (status.includes('cancelled before draft')) {
          cancelledBeforeDraft.count++;
          cancelledBeforeDraft.premium += premium;
        } else if (status.includes('declined')) {
          declined.count++;
          declined.premium += premium;
        } else if (status.includes('lapsed pending')) {
          lapsedPending.count++;
          lapsedPending.premium += premium;
        } else if (status === 'lapsed') {
          lapsed.count++;
          lapsed.premium += premium;
        } else if (status.includes('not taken')) {
          notTaken.count++;
          notTaken.premium += premium;
        }
      });
    });

    return { 
      totalDownlines,
      totalPolicies, 
      totalAnnualPremium, 
      approved, 
      underwriting, 
      followUp, 
      cancelledBeforeDraft,
      declined,
      lapsedPending,
      lapsed,
      notTaken
    };
  };

  const fetchDownlines = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/portal/my-downlines`, {
        params: { agent_id: userInfo?.agent_id }
      });
      
      const downlinesData = Array.isArray(response.data) ? response.data : [];
      setDownlines(downlinesData);
      
      // Calculate dashboard stats
      const stats = calculateDashboardStats(downlinesData);
      setDashboardStats(stats);
      
    } catch (err) {
      console.error('Error fetching downlines:', err);
      toast.error('Failed to load downlines');
    } finally {
      setLoading(false);
    }
  }, [userInfo?.agent_id]);

  useEffect(() => {
    if (userInfo?.agent_id) {
      fetchDownlines();
    }
  }, [userInfo?.agent_id, fetchDownlines]);

  // Group downlines with policy summaries
  const downlinesData = useMemo(() => {
    return downlines.map(downline => {
      const policiesRaw = downline.policies_of_agent || [];
      const policies = Array.isArray(policiesRaw) ? policiesRaw : [];
      const agentId = downline.downline_agentId || downline.id || downline.agent_id;
      
      // Calculate summaries by status
      const statusSummaries = {};
      let totalPolicies = policies.length;
      let totalAnnualPremium = 0;
      
      policies.forEach(policy => {
        const status = policy.policyStatus || 'Unknown';
        const premium = parseFloat(policy.annual_premium) || 0;
        
        if (!statusSummaries[status]) {
          statusSummaries[status] = { count: 0, premium: 0 };
        }
        
        statusSummaries[status].count++;
        statusSummaries[status].premium += premium;
        totalAnnualPremium += premium;
      });
      
      return {
        id: agentId,
        name: `${downline.first_name} ${downline.last_name}`,
        npn: downline.npn,
        status: downline.status,
        totalPolicies,
        totalAnnualPremium,
        statusSummaries,
        rawDownline: downline // Keep reference for debugging
      };
    });
  }, [downlines]);

  // Filter downlines
  const filteredDownlines = useMemo(() => {
    let filtered = downlinesData;
    
    // Search filter
    if (downlineSearch) {
      const searchLower = downlineSearch.toLowerCase();
      filtered = filtered.filter(downline => 
        downline.name?.toLowerCase().includes(searchLower) ||
        downline.npn?.toString().toLowerCase().includes(searchLower)
      );
    }
    
    // Sales filter
    if (downlineSalesFilter === 'with-sales') {
      filtered = filtered.filter(downline => downline.totalPolicies > 0);
    } else if (downlineSalesFilter === 'without-sales') {
      filtered = filtered.filter(downline => downline.totalPolicies === 0);
    }
    
    // Status filter
    if (downlineStatusFilter && downlineStatusFilter.length > 0) {
      filtered = filtered.filter(downline => downlineStatusFilter.includes(downline.status));
    }
    
    return filtered;
  }, [downlinesData, downlineSearch, downlineSalesFilter, downlineStatusFilter]);

  // Paginate downlines
  const paginatedDownlines = useMemo(() => {
    const start = (downlinePage - 1) * downlinesPerPage;
    return filteredDownlines.slice(start, start + downlinesPerPage);
  }, [filteredDownlines, downlinePage, downlinesPerPage]);

  const downlineTotalPages = Math.ceil(filteredDownlines.length / downlinesPerPage);

  // Get all policies (either filtered by selected downline or all)
  const allPolicies = useMemo(() => {
    const policies = [];
    downlines.forEach(downline => {
      const downlinePoliciesRaw = downline.policies_of_agent || [];
      const downlinePolicies = Array.isArray(downlinePoliciesRaw) ? downlinePoliciesRaw : [];
      const agentId = downline.downline_agentId || downline.id || downline.agent_id;
      downlinePolicies.forEach(policy => {
        policies.push({
          ...policy,
          downlineId: agentId,
          downlineName: `${downline.first_name} ${downline.last_name}`,
          policy_splits: Array.isArray(policy.policy_splits) ? policy.policy_splits : []
        });
      });
    });
    return policies;
  }, [downlines]);

  // Filter policies
  const filteredPolicies = useMemo(() => {
    let filtered = allPolicies;
    
    // Filter by selected downline
    if (selectedDownline) {
      filtered = filtered.filter(policy => policy.downlineId === selectedDownline.id);
    }
    
    // Search filter
    if (policySearch) {
      const searchLower = policySearch.toLowerCase();
      filtered = filtered.filter(policy =>
        policy.client?.toLowerCase().includes(searchLower) ||
        policy.policy_number?.toLowerCase().includes(searchLower) ||
        policy.downlineName?.toLowerCase().includes(searchLower)
      );
    }
    
    // Policy Status filter (multi-select)
    if (policyStatusFilter && policyStatusFilter.length > 0) {
      filtered = filtered.filter(policy => policyStatusFilter.includes(policy.policyStatus));
    }
    
    // Paid Status filter (multi-select)
    if (policyPaidStatusFilter && policyPaidStatusFilter.length > 0) {
      filtered = filtered.filter(policy => policyPaidStatusFilter.includes(policy.paid_status));
    }
    
    // Date filter
    if (policyDateRange?.from && policyDateRange?.to) {
      filtered = filtered.filter(policy => {
        if (!policy.created_at) return false;
        const policyDate = new Date(policy.created_at);
        return policyDate >= policyDateRange.from && policyDate <= policyDateRange.to;
      });
    }
    
    return filtered;
  }, [allPolicies, selectedDownline, policySearch, policyStatusFilter, policyPaidStatusFilter, policyDateRange]);

  // Paginate policies
  const paginatedPolicies = useMemo(() => {
    const start = (policyPage - 1) * policiesPerPage;
    return filteredPolicies.slice(start, start + policiesPerPage);
  }, [filteredPolicies, policyPage, policiesPerPage]);

  const policyTotalPages = Math.ceil(filteredPolicies.length / policiesPerPage);

  // Get unique statuses
  const uniquePolicyStatuses = useMemo(() => {
    const statuses = new Set();
    allPolicies.forEach(policy => {
      if (policy.policyStatus) statuses.add(policy.policyStatus);
    });
    return Array.from(statuses);
  }, [allPolicies]);

  const uniquePaidStatuses = useMemo(() => {
    const statuses = new Set();
    allPolicies.forEach(policy => {
      if (policy.paid_status) statuses.add(policy.paid_status);
    });
    return Array.from(statuses);
  }, [allPolicies]);

  const uniqueDownlineStatuses = useMemo(() => {
    const statuses = new Set();
    downlinesData.forEach(downline => {
      if (downline.status) statuses.add(downline.status);
    });
    return Array.from(statuses);
  }, [downlinesData]);

  // Recalculate dashboard stats based on filtered policies
  const filteredDashboardStats = useMemo(() => {
    // Group filtered policies back into downlines structure for calculation
    const downlinesPoliciesMap = {};
    filteredPolicies.forEach(policy => {
      if (!downlinesPoliciesMap[policy.downlineId]) {
        downlinesPoliciesMap[policy.downlineId] = {
          policies_of_agent: []
        };
      }
      downlinesPoliciesMap[policy.downlineId].policies_of_agent.push(policy);
    });
    
    const downlinesWithFilteredPolicies = Object.values(downlinesPoliciesMap);
    return calculateDashboardStats(downlinesWithFilteredPolicies);
  }, [filteredPolicies]);

  const togglePolicy = (policyId) => {
    setExpandedPolicies(prev => ({
      ...prev,
      [policyId]: !prev[policyId]
    }));
  };

  // Format fields with "Other" handling
  const formatWithOther = (mainValue, customValue) => {
    if (!mainValue || mainValue === 'na') return 'N/A';
    if (mainValue.toLowerCase() === 'other' && customValue && customValue !== 'na') {
      return `Other - ${customValue}`;
    }
    return mainValue;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      return format(new Date(timestamp), 'MMM dd, yyyy');
    } catch {
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#d4af37]" />
      </div>
    );
  }

  if (!downlines || downlines.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <Users className="w-16 h-16 text-neutral-300" />
        <p className="text-neutral-500 text-lg">No downlines found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold text-neutral-800">My Downlines</h2>
        <p className="text-neutral-600 mt-1">View your downline agents and their policies</p>
      </div>

      {/* Dashboard Stats - Modern Design */}
      <div className="grid grid-cols-4 gap-6 mt-6">
        {/* Total Downlines - Hero Card */}
        <div className="relative overflow-hidden bg-gradient-to-br from-[#d4af37] via-[#f4e5b8] to-[#fffef0] p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-[#d4af37]/20">
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-neutral-700 uppercase tracking-wider">Total Downlines</p>
              <Users className="w-5 h-5 text-[#b8941e]" />
            </div>
            <p className="text-4xl font-black text-[#b8941e] mb-1">{filteredDashboardStats.totalDownlines}</p>
            <p className="text-xs text-neutral-600 font-medium">{filteredDashboardStats.totalPolicies} Policies</p>
          </div>
          <div className="absolute -right-4 -bottom-4 opacity-10">
            <div className="w-24 h-24 bg-[#d4af37] rounded-full"></div>
          </div>
        </div>

        {/* Approved */}
        <div className="relative overflow-hidden bg-gradient-to-br from-emerald-400 via-green-300 to-green-100 p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-green-900 uppercase tracking-wider">Approved</p>
              <CheckCircle2 className="w-5 h-5 text-green-700" />
            </div>
            <p className="text-3xl font-black text-green-900 mb-1">{filteredDashboardStats.approved.count}</p>
            <p className="text-sm font-bold text-green-700">${filteredDashboardStats.approved.premium.toLocaleString()}</p>
          </div>
          <div className="absolute -right-6 -bottom-6 opacity-10">
            <CheckCircle2 className="w-32 h-32 text-green-900" />
          </div>
        </div>

        {/* Underwriting */}
        <div className="relative overflow-hidden bg-gradient-to-br from-blue-400 via-blue-300 to-blue-100 p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-blue-900 uppercase tracking-wider">Underwriting</p>
              <Clock className="w-5 h-5 text-blue-700" />
            </div>
            <p className="text-3xl font-black text-blue-900 mb-1">{filteredDashboardStats.underwriting.count}</p>
            <p className="text-sm font-bold text-blue-700">${filteredDashboardStats.underwriting.premium.toLocaleString()}</p>
          </div>
          <div className="absolute -right-6 -bottom-6 opacity-10">
            <Clock className="w-32 h-32 text-blue-900" />
          </div>
        </div>

        {/* Total Premium */}
        <div className="relative overflow-hidden bg-gradient-to-br from-amber-400 via-yellow-300 to-amber-100 p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-amber-900 uppercase tracking-wider">Total Premium</p>
              <DollarSign className="w-5 h-5 text-amber-700" />
            </div>
            <p className="text-2xl font-black text-amber-900 mb-1">${filteredDashboardStats.totalAnnualPremium.toLocaleString()}</p>
            <p className="text-xs text-amber-700 font-medium">Annual Revenue</p>
          </div>
          <div className="absolute -right-6 -bottom-6 opacity-10">
            <DollarSign className="w-32 h-32 text-amber-900" />
          </div>
        </div>
      </div>

      {/* Dashboard Stats - Row 2 - Compact Status Cards */}
      <div className="grid grid-cols-5 gap-4 mt-4">
        <div className="bg-white border-2 border-red-200 p-4 rounded-xl hover:border-red-400 transition-all duration-200 hover:shadow-md">
          <p className="text-xs font-semibold text-red-600 mb-1">Cancelled</p>
          <p className="text-2xl font-bold text-red-700">{filteredDashboardStats.cancelledBeforeDraft.count}</p>
          <p className="text-xs text-red-500 font-medium">${filteredDashboardStats.cancelledBeforeDraft.premium.toLocaleString()}</p>
        </div>
        <div className="bg-white border-2 border-orange-200 p-4 rounded-xl hover:border-orange-400 transition-all duration-200 hover:shadow-md">
          <p className="text-xs font-semibold text-orange-600 mb-1">Declined</p>
          <p className="text-2xl font-bold text-orange-700">{filteredDashboardStats.declined.count}</p>
          <p className="text-xs text-orange-500 font-medium">${filteredDashboardStats.declined.premium.toLocaleString()}</p>
        </div>
        <div className="bg-white border-2 border-yellow-200 p-4 rounded-xl hover:border-yellow-400 transition-all duration-200 hover:shadow-md">
          <p className="text-xs font-semibold text-yellow-600 mb-1">Lapsed Pending</p>
          <p className="text-2xl font-bold text-yellow-700">{filteredDashboardStats.lapsedPending.count}</p>
          <p className="text-xs text-yellow-600 font-medium">${filteredDashboardStats.lapsedPending.premium.toLocaleString()}</p>
        </div>
        <div className="bg-white border-2 border-purple-200 p-4 rounded-xl hover:border-purple-400 transition-all duration-200 hover:shadow-md">
          <p className="text-xs font-semibold text-purple-600 mb-1">Lapsed</p>
          <p className="text-2xl font-bold text-purple-700">{filteredDashboardStats.lapsed.count}</p>
          <p className="text-xs text-purple-500 font-medium">${filteredDashboardStats.lapsed.premium.toLocaleString()}</p>
        </div>
        <div className="bg-white border-2 border-neutral-300 p-4 rounded-xl hover:border-neutral-500 transition-all duration-200 hover:shadow-md">
          <p className="text-xs font-semibold text-neutral-600 mb-1">Not Taken</p>
          <p className="text-2xl font-bold text-neutral-700">{filteredDashboardStats.notTaken.count}</p>
          <p className="text-xs text-neutral-500 font-medium">${filteredDashboardStats.notTaken.premium.toLocaleString()}</p>
        </div>
      </div>

      {/* Two-Column Layout */}
      <div className="flex gap-6" style={{ minHeight: '600px' }}>
        
        {/* Left Container - Downlines Table (35%) */}
        <Card className="border-0 shadow-sm rounded-xl" style={{ width: '35%' }}>
          <CardContent className="p-6">
            <div className="mb-4">
              <h3 className="text-lg font-bold text-neutral-800">Downline Agents</h3>
              <p className="text-xs text-neutral-500 mt-1">{filteredDownlines.length} total</p>
            </div>

            {/* Filters with Labels */}
            <div className="space-y-3 mb-4">
              {/* Search Bar - 50vw */}
              <div style={{ width: '100%' }}>
                <label className="text-xs text-neutral-500 font-medium mb-1 block">Quick Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-4 h-4" />
                  <Input
                    type="text"
                    placeholder="Search agents..."
                    value={downlineSearch}
                    onChange={(e) => {
                      setDownlineSearch(e.target.value);
                      setDownlinePage(1);
                    }}
                    className="pl-9 rounded-lg text-sm"
                  />
                </div>
              </div>

              {/* Sales Filter and Status Filter in Row */}
              <div className="flex gap-2">
                {/* Sales Filter - 50% */}
                <div style={{ width: '50%' }}>
                  <label className="text-xs text-neutral-500 font-medium mb-1 block">Filter by Sales</label>
                  <Select
                    value={downlineSalesFilter}
                    onValueChange={(value) => {
                      setDownlineSalesFilter(value);
                      setDownlinePage(1);
                    }}
                  >
                    <SelectTrigger className="rounded-lg text-sm">
                      <SelectValue placeholder="All Agents" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Agents</SelectItem>
                      <SelectItem value="with-sales">With Sales</SelectItem>
                      <SelectItem value="without-sales">Without Sales</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Status Filter - 50% */}
                <div style={{ width: '50%' }}>
                  <label className="text-xs text-neutral-500 font-medium mb-1 block">Filter by Status</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="rounded-lg w-full text-sm justify-start">
                        <Filter className="w-4 h-4 mr-2" />
                        Status {downlineStatusFilter.length > 0 && `(${downlineStatusFilter.length})`}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 p-3" align="start">
                      <div className="space-y-2">
                        <div className="font-semibold text-sm mb-2">Select Status</div>
                        {uniqueDownlineStatuses.map(status => (
                          <div key={status} className="flex items-center space-x-2">
                            <Checkbox
                              id={`downline-status-${status}`}
                              checked={downlineStatusFilter.includes(status)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setDownlineStatusFilter([...downlineStatusFilter, status]);
                                } else {
                                  setDownlineStatusFilter(downlineStatusFilter.filter(s => s !== status));
                                }
                                setDownlinePage(1);
                              }}
                            />
                            <label
                              htmlFor={`downline-status-${status}`}
                              className="text-sm cursor-pointer flex-1 capitalize"
                            >
                              {status}
                            </label>
                          </div>
                        ))}
                        {downlineStatusFilter.length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setDownlineStatusFilter([]);
                              setDownlinePage(1);
                            }}
                            className="w-full mt-2 text-xs"
                          >
                            Clear All
                          </Button>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>

            {/* Clear Filters & Show All */}
            <div className="flex gap-2 mb-3">
              {(downlineSearch || downlineSalesFilter !== 'all' || downlineStatusFilter.length > 0) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setDownlineSearch('');
                    setDownlineSalesFilter('all');
                    setDownlineStatusFilter([]);
                    setDownlinePage(1);
                  }}
                  className="text-xs text-neutral-500 hover:text-neutral-700"
                >
                  Clear Filters
                </Button>
              )}
              {filteredDownlines.length > 0 && selectedDownline && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedDownline(null);
                    setPolicyPage(1);
                  }}
                  className="text-xs text-[#d4af37] hover:bg-[#f4e5b8]"
                >
                  Show All Policies
                </Button>
              )}
            </div>

            {/* Downlines List */}
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {paginatedDownlines.length === 0 ? (
                <p className="text-sm text-neutral-500 text-center py-8">No downlines found</p>
              ) : (
                paginatedDownlines.map((downline) => (
                  <div
                    key={downline.id}
                    onClick={() => {
                      setSelectedDownline(selectedDownline?.id === downline.id ? null : downline);
                      setPolicyPage(1);
                    }}
                    className={`cursor-pointer p-3 rounded-lg border transition-all duration-200 ${
                      selectedDownline?.id === downline.id
                        ? 'bg-[#fffef0] border-[#d4af37] shadow-sm'
                        : 'bg-white border-neutral-200 hover:bg-neutral-50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-neutral-800">{downline.name}</p>
                        <p className="text-xs text-neutral-500">NPN: {downline.npn || 'N/A'}</p>
                      </div>
                      <Badge 
                        className={`text-xs ${
                          downline.status === 'active' 
                            ? 'bg-green-100 text-green-800 hover:bg-green-100' 
                            : 'bg-neutral-200 text-neutral-700'
                        }`}
                      >
                        {downline.status || 'Unknown'}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <p className="text-neutral-500">Policies</p>
                        <p className="font-semibold text-neutral-800">{downline.totalPolicies}</p>
                      </div>
                      <div>
                        <p className="text-neutral-500">Total Premium</p>
                        <p className="font-semibold text-[#d4af37]">
                          ${downline.totalAnnualPremium.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    
                    {/* Status breakdown */}
                    {Object.keys(downline.statusSummaries).length > 0 && (
                      <div className="mt-2 pt-2 border-t border-neutral-200">
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(downline.statusSummaries).map(([status, data]) => (
                            <Badge key={status} variant="outline" className="text-xs">
                              {status}: {data.count}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Downlines Pagination */}
            {downlineTotalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-neutral-200">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDownlinePage(prev => Math.max(1, prev - 1))}
                  disabled={downlinePage === 1}
                  className="text-xs h-8 rounded-lg"
                >
                  Previous
                </Button>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-neutral-600 font-medium">
                    Page {downlinePage} of {downlineTotalPages}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-neutral-500">Rows:</span>
                    <Select 
                      value={downlinesPerPage.toString()} 
                      onValueChange={(value) => {
                        setDownlinesPerPage(Number(value));
                        setDownlinePage(1);
                      }}
                    >
                      <SelectTrigger className="w-20 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDownlinePage(prev => Math.min(downlineTotalPages, prev + 1))}
                  disabled={downlinePage === downlineTotalPages}
                  className="text-xs h-8 rounded-lg"
                >
                  Next
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right Container - Policies Table (65%) */}
        <Card className="border-0 shadow-sm rounded-xl" style={{ width: '65%' }}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-neutral-800">Policies</h3>
                <p className="text-xs text-neutral-500 mt-1">
                  {selectedDownline ? `Filtered by ${selectedDownline.name}` : `${filteredPolicies.length} total`}
                </p>
              </div>
            </div>

            {/* Policy Filters with Labels and VW Widths */}
            <div className="flex gap-3 mb-4">
              {/* Search Bar - 40vw */}
              <div style={{ width: '40vw' }}>
                <label className="text-xs text-neutral-500 font-medium mb-1 block">Quick Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-4 h-4" />
                  <Input
                    type="text"
                    placeholder="Search policies..."
                    value={policySearch}
                    onChange={(e) => {
                      setPolicySearch(e.target.value);
                      setPolicyPage(1);
                    }}
                    className="pl-9 rounded-xl text-sm"
                  />
                </div>
              </div>

              {/* Policy Status Filter - 15vw */}
              <div style={{ width: '15vw' }}>
                <label className="text-xs text-neutral-500 font-medium mb-1 block">Filter by Policy Status</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="rounded-xl w-full text-sm justify-start">
                      <Filter className="w-4 h-4 mr-2" />
                      Policy Status {policyStatusFilter.length > 0 && `(${policyStatusFilter.length})`}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-3" align="start">
                    <div className="space-y-2">
                      <div className="font-semibold text-sm mb-2">Select Policy Status</div>
                      {uniquePolicyStatuses.map(status => (
                        <div key={status} className="flex items-center space-x-2">
                          <Checkbox
                            id={`policy-status-${status}`}
                            checked={policyStatusFilter.includes(status)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setPolicyStatusFilter([...policyStatusFilter, status]);
                              } else {
                                setPolicyStatusFilter(policyStatusFilter.filter(s => s !== status));
                              }
                              setPolicyPage(1);
                            }}
                          />
                          <label
                            htmlFor={`policy-status-${status}`}
                            className="text-sm cursor-pointer flex-1"
                          >
                            {status}
                          </label>
                        </div>
                      ))}
                      {policyStatusFilter.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setPolicyStatusFilter([]);
                            setPolicyPage(1);
                          }}
                          className="w-full mt-2 text-xs"
                        >
                          Clear All
                        </Button>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Paid Status Filter - 15vw */}
              <div style={{ width: '15vw' }}>
                <label className="text-xs text-neutral-500 font-medium mb-1 block">Filter by Paid Status</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="rounded-xl w-full text-sm justify-start">
                      <Filter className="w-4 h-4 mr-2" />
                      Paid Status {policyPaidStatusFilter.length > 0 && `(${policyPaidStatusFilter.length})`}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-3" align="start">
                    <div className="space-y-2">
                      <div className="font-semibold text-sm mb-2">Select Paid Status</div>
                      {uniquePaidStatuses.map(status => (
                        <div key={status} className="flex items-center space-x-2">
                          <Checkbox
                            id={`paid-status-${status}`}
                            checked={policyPaidStatusFilter.includes(status)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setPolicyPaidStatusFilter([...policyPaidStatusFilter, status]);
                              } else {
                                setPolicyPaidStatusFilter(policyPaidStatusFilter.filter(s => s !== status));
                              }
                              setPolicyPage(1);
                            }}
                          />
                          <label
                            htmlFor={`paid-status-${status}`}
                            className="text-sm cursor-pointer flex-1"
                          >
                            {status}
                          </label>
                        </div>
                      ))}
                      {policyPaidStatusFilter.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setPolicyPaidStatusFilter([]);
                            setPolicyPage(1);
                          }}
                          className="w-full mt-2 text-xs"
                        >
                          Clear All
                        </Button>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Date Range Filter - 30vw */}
              <div style={{ width: '30vw' }}>
                <label className="text-xs text-neutral-500 font-medium mb-1 block">Filter by Date Created</label>
                <DateRangePicker
                  value={policyDateRange}
                  onChange={(range) => {
                    setPolicyDateRange(range);
                    setPolicyPage(1);
                  }}
                />
              </div>
            </div>

            {(policySearch || policyStatusFilter.length > 0 || policyPaidStatusFilter.length > 0 || policyDateRange) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setPolicySearch('');
                  setPolicyStatusFilter([]);
                  setPolicyPaidStatusFilter([]);
                  setPolicyDateRange(null);
                  setPolicyPage(1);
                }}
                className="mb-3 text-xs rounded-xl"
              >
                Clear All Filters
              </Button>
            )}

            {/* Policies Table */}
            <div className="overflow-x-auto">
              <div className="max-h-[520px] overflow-y-auto rounded-xl border border-neutral-100">
                {paginatedPolicies.length === 0 ? (
                  <div className="text-center py-12 text-neutral-400">
                    <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No policies found</p>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-gradient-to-r from-neutral-50 to-neutral-100 sticky top-0 z-10">
                      <tr>
                        <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5 w-8 first:rounded-tl-xl"></th>
                        <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5">Client</th>
                        <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5">Policy #</th>
                        <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5">Carrier</th>
                        <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5">Premium</th>
                        <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5">Status</th>
                        <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5 last:rounded-tr-xl">Splits</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100 bg-white">
                      {paginatedPolicies.map((policy) => (
                        <React.Fragment key={policy.id}>
                          <tr className="hover:bg-neutral-50 transition-all duration-200">
                            <td className="px-4 py-3">
                              <button
                                onClick={() => togglePolicy(policy.id)}
                                className="text-neutral-400 hover:text-[#d4af37]"
                              >
                                {expandedPolicies[policy.id] ? (
                                  <ChevronDown className="w-5 h-5" />
                                ) : (
                                  <ChevronRight className="w-5 h-5" />
                                )}
                              </button>
                            </td>
                            <td className="px-4 py-3">
                              <p className="font-medium text-neutral-800">{policy.client || 'N/A'}</p>
                              <p className="text-xs text-neutral-500">{formatDate(policy.created_at)}</p>
                            </td>
                            <td className="px-4 py-3">
                              <p className="font-mono text-sm text-neutral-700">{policy.policy_number || 'N/A'}</p>
                            </td>
                            <td className="px-4 py-3 text-sm text-neutral-700">
                              {formatWithOther(policy.policyCarrier, policy.custom_carrier)}
                            </td>
                            <td className="px-4 py-3">
                              <p className="font-semibold text-neutral-800">${parseFloat(policy.annual_premium || 0).toLocaleString()}</p>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-col gap-1">
                                {policy.policyStatus && (
                                  <Badge className="bg-[#f4e5b8] text-[#b8941e] hover:bg-[#f4e5b8] text-xs w-fit">
                                    {policy.policyStatus}
                                  </Badge>
                                )}
                                {policy.isLocked && (
                                  <Badge variant="outline" className="border-red-300 text-red-600 text-xs w-fit px-1.5">
                                    <Lock className="w-3 h-3" />
                                  </Badge>
                                )}
                                {policy.paid_status && (
                                  <Badge variant="outline" className="border-blue-300 text-blue-600 text-xs w-fit">
                                    {policy.paid_status}
                                  </Badge>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              {policy.policy_splits && policy.policy_splits.length > 0 && (
                                <Badge variant="outline" className="border-[#d4af37] text-[#b8941e]">
                                  <Users className="w-3 h-3 mr-1" />
                                  {policy.policy_splits.length}
                                </Badge>
                              )}
                            </td>
                          </tr>

                          {/* Expanded Policy Details */}
                          {expandedPolicies[policy.id] && (
                            <tr>
                              <td colSpan="7" className="bg-neutral-50 px-4 py-6">
                                <h4 className="text-sm font-semibold text-neutral-700 mb-3">Policy Details</h4>
                                <div className="grid grid-cols-3 gap-4 mb-4">
                                  <div>
                                    <p className="text-xs text-neutral-500">Agent</p>
                                    <p className="text-sm text-neutral-800">{policy.downlineName || 'N/A'}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-neutral-500">Carrier</p>
                                    <p className="text-sm text-neutral-800">{formatWithOther(policy.policyCarrier, policy.custom_carrier)}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-neutral-500">Product</p>
                                    <p className="text-sm text-neutral-800">{policy.carrier_product || 'N/A'}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-neutral-500">Effective Date</p>
                                    <p className="text-sm text-neutral-800">{policy.initial_draft_date || 'N/A'}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-neutral-500">Paid Status</p>
                                    <p className="text-sm text-neutral-800">{policy.paid_status || 'N/A'}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-neutral-500">State</p>
                                    <p className="text-sm text-neutral-800">{policy.state || 'N/A'}</p>
                                  </div>
                                </div>

                                {/* Splits Information */}
                                {policy.policy_splits && policy.policy_splits.length > 0 && (
                                  <div className="mt-4 pt-4 border-t border-neutral-300">
                                    <h5 className="text-sm font-semibold text-neutral-700 mb-2 flex items-center gap-2">
                                      <Users className="w-4 h-4" />
                                      Commission Splits ({policy.policy_splits.length})
                                    </h5>
                                    <div className="space-y-2">
                                      {policy.policy_splits.map((split, idx) => (
                                        <div key={idx} className="bg-white p-3 rounded-lg border border-neutral-200">
                                          <div className="flex items-center justify-between">
                                            <div>
                                              <p className="text-xs text-neutral-500">Agent</p>
                                              <p className="text-sm font-medium text-neutral-800">
                                                {split.onsplit_name || 'N/A'}
                                              </p>
                                              <p className="text-xs text-neutral-500">NPN: {split.onsplit_npn || 'N/A'}</p>
                                            </div>
                                            <div className="text-right">
                                              <p className="text-lg font-bold text-[#d4af37]">
                                                {split.agent_on_split_percent || split.split_percentage || 0}%
                                              </p>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {(!policy.policy_splits || policy.policy_splits.length === 0) && (
                                  <div className="mt-4 pt-4 border-t border-neutral-300 text-center">
                                    <p className="text-sm text-neutral-500">No commission splits for this policy</p>
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Policies Pagination */}
            {policyTotalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-neutral-200">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPolicyPage(prev => Math.max(1, prev - 1))}
                  disabled={policyPage === 1}
                  className="text-xs h-8 rounded-lg"
                >
                  Previous
                </Button>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-neutral-600 font-medium">
                    Page {policyPage} of {policyTotalPages}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-neutral-500">Rows:</span>
                    <Select 
                      value={policiesPerPage.toString()} 
                      onValueChange={(value) => {
                        setPoliciesPerPage(Number(value));
                        setPolicyPage(1);
                      }}
                    >
                      <SelectTrigger className="w-20 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPolicyPage(prev => Math.min(policyTotalPages, prev + 1))}
                  disabled={policyPage === policyTotalPages}
                  className="text-xs h-8 rounded-lg"
                >
                  Next
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
